import { HttpClient } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { Observable } from 'rxjs'
import { tap } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { UiSkinKey } from '../../skins/skin.type'
import { resolveUiSkinKey, UI_SKIN_KEYS, UI_SKIN_OPTIONS } from '../../skins/skin-registry'
import { ApiResponse } from '../types/response.type'
import { DomainService } from './domain.service'
import { HelperService } from './helper.service'

const API_URL = environment.apiUrl
const USER_UI_SKIN_STORAGE_KEY_PREFIX = 'ui_skin_key'

@Injectable({
	providedIn: 'root',
})
export class WhiteLabelService {
	http = inject(HttpClient)

	domainService = inject(DomainService)

	helperService = inject(HelperService)

	isSelfRegistrationEnabled = signal<boolean>(false)

	isTurnstileEnabled = signal<boolean>(false)

	uiSkinKey = signal<UiSkinKey>('v1')

	adminUiSkinKey = signal<UiSkinKey>('v1')

	readonly uiSkinOptions = UI_SKIN_OPTIONS

	constructor() {
		this.helperService.userDataChanged$.subscribe(() => this.resolveActiveSkin())
	}

	activeSkin(): UiSkinKey {
		return this.uiSkinKey()
	}

	fetchPermissions(): Observable<
		ApiResponse<{ SELF_REGISTER_ALLOWED: boolean; LOGIN_HUMAN_VERIFICATION?: boolean; UI_SKIN_KEY?: string; ui_skin_key?: string }[]>
	> {
		return this.http
			.get<
				ApiResponse<{ SELF_REGISTER_ALLOWED: boolean; LOGIN_HUMAN_VERIFICATION?: boolean; UI_SKIN_KEY?: string; ui_skin_key?: string }[]>
			>(`${API_URL}/api/white_label/get_permission/${this.domainService.domain}`)
			.pipe(
				tap((response) => {
					if (response?.status === 'ok') {
						const selfRegistrationEnabled = response?.result?.[0]?.SELF_REGISTER_ALLOWED ?? false
						this.isSelfRegistrationEnabled.set(selfRegistrationEnabled)
						const turnstileEnabled = !!response?.result?.[0]?.LOGIN_HUMAN_VERIFICATION
						this.isTurnstileEnabled.set(turnstileEnabled)
						this.adminUiSkinKey.set(resolveUiSkinKey(response?.result?.[0]?.UI_SKIN_KEY ?? response?.result?.[0]?.ui_skin_key))
						this.resolveActiveSkin()
					}
				}),
			)
	}

	canUserSelectTheme(): boolean {
		const permissions = this.helperService.getUserData()?.user_fields?.PERMISSIONS ?? ''
		return permissions
			.split(',')
			.map((permission) => permission.trim())
			.includes('enableUserThemeSelection')
	}

	setUserUiSkin(uiSkinKey: string): boolean {
		const storageKey = this.getUserUiSkinStorageKey()
		if (!storageKey || !UI_SKIN_KEYS.includes(uiSkinKey as UiSkinKey) || uiSkinKey === this.activeSkin()) return false

		localStorage.setItem(storageKey, uiSkinKey)
		this.uiSkinKey.set(uiSkinKey as UiSkinKey)
		return true
	}

	private resolveActiveSkin(): void {
		const storageKey = this.getUserUiSkinStorageKey()
		const userSkin = storageKey ? localStorage.getItem(storageKey)?.trim() : undefined
		const activeSkin = this.canUserSelectTheme() && UI_SKIN_KEYS.includes(userSkin as UiSkinKey) ? (userSkin as UiSkinKey) : this.adminUiSkinKey()
		this.uiSkinKey.set(activeSkin)
	}

	private getUserUiSkinStorageKey(): string | null {
		const userFields = this.helperService.getUserData()?.user_fields
		if (!userFields?.ID) return null

		return `${USER_UI_SKIN_STORAGE_KEY_PREFIX}:${userFields.DOMAIN_ID}:${userFields.ID}`
	}

	isDefaultSkin(): boolean {
		return this.uiSkinKey() === 'v1'
	}

	isV2Skin(): boolean {
		return this.uiSkinKey() === 'v2'
	}

	isV3Skin(): boolean {
		return this.uiSkinKey() === 'v3'
	}

	usesInlineBetPlacement(): boolean {
		return !this.isV2Skin()
	}
}
